import React from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart3,
  BriefcaseBusiness,
  ClipboardList,
  Handshake,
  Network,
  Timer,
} from "lucide-react";

function EmpregabilidadePublicoPage() {
  const navigate = useNavigate();

  return (
    <div className="w-full py-6">
      <div className="mx-auto w-full max-w-[1400px] px-3 md:px-6 2xl:px-0">
        {/* Hero */}
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="bg-gradient-to-r from-emerald-50 via-white to-emerald-50 px-6 py-6 md:px-8 md:py-8">
            <div className="flex items-start justify-between gap-6">
              <div className="min-w-0">
                <div className="text-2xl font-bold tracking-tight text-gray-900 md:text-3xl">
                  Empregabilidade
                </div>
                <div className="mt-2 max-w-3xl text-base leading-7 text-gray-600 md:text-lg">
                  A Universidade Eduardo Mondlane reforça o seu compromisso com o
                  acompanhamento dos graduados, promovendo a sua inserção
                  profissional e o alinhamento com as dinâmicas do mercado de
                  trabalho, com base em evidência.
                </div>
              </div>

              <div className="hidden shrink-0 items-center gap-2 md:flex">
                <div className="rounded-xl bg-white/70 p-3 ring-1 ring-emerald-100">
                  <BriefcaseBusiness className="h-6 w-6 text-uem-primary" />
                </div>
                <div className="rounded-xl bg-white/70 p-3 ring-1 ring-emerald-100">
                  <BarChart3 className="h-6 w-6 text-uem-primary" />
                </div>
              </div>
            </div>

            {/* CTA principal fica no bloco “Próximo passo” (mais abaixo). */}
          </div>
        </div>

        {/* Porque existe */}
        <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm md:p-8">
          <div className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            Contexto e propósito
          </div>
          <div className="mt-2 text-lg font-semibold text-gray-900">
            Uma resposta institucional às transformações do mercado
          </div>
          <div className="mt-3 max-w-5xl whitespace-pre-line text-justify text-base leading-7 text-gray-600">
            O contexto nacional e internacional caracteriza-se por rápidas
            transformações económicas, tecnológicas e organizacionais. Este
            cenário exige das universidades o reforço sistemático da
            empregabilidade dos seus graduados, o alinhamento com o mercado de
            trabalho e a consolidação de mecanismos de garantia da qualidade com
            base em evidência.
          </div>

          <div className="mt-5 h-[2px] w-full bg-gray-100" />

          <div className="mt-5 text-sm font-semibold uppercase tracking-wide text-gray-500">
            O que a Plataforma Alumni viabiliza
          </div>
          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-uem-primary">
                  <BarChart3 className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900">
                    Inserção profissional
                  </div>
                  <div className="mt-1 text-sm text-gray-600">
                    Acompanhamento da taxa de inserção por áreas científicas,
                    níveis de formação e dinâmicas sectoriais.
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-uem-primary">
                  <Timer className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900">
                    Transição para o primeiro emprego
                  </div>
                  <div className="mt-1 text-sm text-gray-600">
                    Monitoria dos períodos de transição e atenção a casos de
                    inserção tardia após a conclusão do curso.
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-uem-primary">
                  <Network className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900">
                    Fatores facilitadores
                  </div>
                  <div className="mt-1 text-sm text-gray-600">
                    Análise do papel de redes de contacto, estágios, experiências
                    práticas e participação em projectos.
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-uem-primary">
                  <ClipboardList className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900">
                    Evidência e rastreamento
                  </div>
                  <div className="mt-1 text-sm text-gray-600">
                    Atualização periódica de dados e produção de evidências para
                    suporte à tomada de decisão institucional.
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:col-span-2 lg:col-span-2">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-uem-primary">
                  <Handshake className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900">
                    Articulação UEM–mercado de trabalho
                  </div>
                  <div className="mt-1 text-sm text-gray-600">
                    Identificação de constrangimentos, oportunidades de melhoria
                    e sectores estratégicos de cooperação.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Separador de fim de descrição */}
        <div className="mt-6 flex items-center gap-3">
          <div className="h-[2px] w-full bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
        </div>

        {/* Próximo passo */}
        <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm md:p-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="min-w-0">
              <div className="text-lg font-semibold text-gray-900">
                Próximo passo
              </div>
              <div className="mt-1 max-w-3xl text-sm text-gray-600">
                Se és graduado da UEM, entra na plataforma para aceder
                à área de Empregabilidade, preencher inquéritos e ajudar a UEM a
                acompanhar melhor o percurso profissional dos seus graduados.
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate("/empregabilidade")}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-5 py-2.5 text-sm font-semibold text-uem-primary transition hover:bg-emerald-100"
            >
              <BriefcaseBusiness className="h-4 w-4" />
              Entrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmpregabilidadePublicoPage;